import React, { useEffect, useState } from "react";

import { Box, Typography, useTheme } from "@mui/material";

interface AvaTextLoadingProps {
  text?: string;
}

const TYPING_SPEED = 18; // ms per character

const AvaTextLoading: React.FC<AvaTextLoadingProps> = ({ text = "Thinking" }) => {
  const theme = useTheme();
  const [displayedText, setDisplayedText] = useState("");
  const [isTypingDone, setIsTypingDone] = useState(false);

  // Typewriter effect
  useEffect(() => {
    setDisplayedText("");
    setIsTypingDone(false);
    if (!text) return;
    let currentIndex = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const type = () => {
      if (currentIndex <= text.length) {
        setDisplayedText(text.slice(0, currentIndex));
        currentIndex++;
        if (currentIndex <= text.length) {
          timeoutId = setTimeout(type, TYPING_SPEED);
        } else {
          setIsTypingDone(true);
        }
      }
    };
    type();
    // Cleanup on text change
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      setDisplayedText("");
      setIsTypingDone(false);
    };
  }, [text]);

  // Helper to render animated dots
  const renderAnimatedDots = () => {
    if (!isTypingDone) return null;
    return (
      <span data-cy="ava-text-loading-dots" style={{ display: "inline-block", marginLeft: 3 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              display: "inline-block",
              width: 3,
              height: 3,
              marginRight: 2,
              marginTop: 7,
              borderRadius: "50%",
              background: theme.palette.text.primary,
              transform: "scale(0.8)",
              animation: `ava-dot-pulse 1.2s infinite`,
              animationDelay: `${i * 0.2}s`,
              verticalAlign: "middle",
            }}
          />
        ))}
        <style>{`
          @keyframes ava-dot-pulse {
            0%, 80%, 100% { transform: scale(0.8); opacity: 0.2; }
            40% { transform: scale(1.1); opacity: 0.6; }
          }
        `}</style>
      </span>
    );
  };

  return (
    <Box
      sx={{
        height: 48,
      }}
    >
      <Typography variant="body1" fontSize={14} color="text.secondary" data-cy="ava-text-loading-typewriter">
        {displayedText}
        {renderAnimatedDots()}
      </Typography>
    </Box>
  );
};

export default AvaTextLoading;
